import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { UpgradeSubscriptionButton } from "components/action-buttons/action-buttons";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import { PermissionsWrapper } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import * as Routes from "routes";
import { ClipConfigProps, ClipStepsControlProps } from "../../clip-menu";
import { ClipConfiguration } from "../../models";
import { validateClipName } from "../../validation";
import { HistoricClips } from "./historic-clips";
import { RecentClips } from "./recent-clips";

const CLIP_ITEM_HEIGHT = 100; // minHeight from ClipItem styles
const GAP_SIZE = 8; // gap: 1 = 8px in MUI

export const MAX_CLIPS = 10; // Used for pagination in HistoricClips

export const NewClip = (props: ClipConfigProps & ClipStepsControlProps) => {
  const [showMore, setShowMore] = useState(false);
  const [maxClips, setMaxClips] = useState(5);
  const containerRef = useRef<HTMLDivElement>(null);
  const newSectionRef = useRef<HTMLDivElement>(null);
  const maxClipsRef = useRef(maxClips);
  const { isPermissionsSuccess } = usePermissions();
  const history = useHistory();
  const [nameError, setNameError] = useState<string | null>(null);

  // Keep ref in sync with state
  useEffect(() => {
    maxClipsRef.current = maxClips;
  }, [maxClips]);

  useEffect(() => {
    const calculateMaxClips = () => {
      if (!containerRef.current || !newSectionRef.current) return;

      const containerHeight = containerRef.current.clientHeight;
      const newSectionHeight = newSectionRef.current.clientHeight;

      // Don't calculate if container doesn't have a valid height yet
      if (containerHeight === 0) return;

      const recentHeaderHeight = 40; // Approximate height for "Recent" Typography
      const moreButtonHeight = 40; // Reserve space for "More" button

      // Available height for clip items
      const availableHeight =
        containerHeight -
        newSectionHeight -
        recentHeaderHeight -
        moreButtonHeight -
        16; // 16px extra padding

      // Calculate how many items can fit
      const itemsCount = Math.floor(
        availableHeight / (CLIP_ITEM_HEIGHT + GAP_SIZE)
      );

      // Ensure at least 1 item, max reasonable number
      const newMaxClips = Math.max(1, Math.min(itemsCount, props.clips.length));

      // Use ref to get current value, avoiding stale closure
      if (newMaxClips !== maxClipsRef.current) {
        setMaxClips(newMaxClips);
      }
    };

    // Calculate when both clips are loaded and permissions are ready
    if (props.clips.length > 0 && isPermissionsSuccess) {
      calculateMaxClips();
    }

    // Recalculate on window resize
    window.addEventListener("resize", calculateMaxClips);

    return () => {
      window.removeEventListener("resize", calculateMaxClips);
    };
  }, [props.clips.length, isPermissionsSuccess]);

  const createNewClipContent = () => {
    return (
      <Box
        ref={newSectionRef}
        sx={{
          marginBottom: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Typography variant="h5">New</Typography>
        <TextField
          fullWidth
          id="clip-name"
          inputProps={{ maxLength: 200 }}
          label="Name"
          value={props.clipConfig.name}
          onChange={(event) => {
            const newName = event.target.value;
            props.setClipConfig((prev: ClipConfiguration) => ({
              ...prev,
              name: newName,
            }));
            // Validate on change
            const error = validateClipName(newName);
            setNameError(error);
          }}
          onKeyDown={(ev) => {
            if (ev.key === "Enter" && !!props.clipConfig.name && !nameError) {
              props.draw.deleteAll();
              props.setActiveStep(
                Math.min(props.activeStep + 1, props.maxSteps)
              );
              ev.preventDefault();
            }
          }}
          error={!!nameError}
          helperText={nameError}
          variant="outlined"
          size="small"
        />
        <Button
          sx={{ alignSelf: "flex-end" }}
          variant="text"
          disabled={!props.clipConfig.name || !!nameError}
          onClick={() => {
            props.draw.deleteAll();
            props.setActiveStep(Math.min(props.activeStep + 1, props.maxSteps));
          }}
          size="small"
        >
          Create
        </Button>
      </Box>
    );
  };

  return (
    <Box
      ref={containerRef}
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <PermissionsWrapper
        keys={[PermissionsKeys.clipCreate]}
        messages={{
          // ROLE_DENY just needs a message when they got 0 clips to make the empty space
          // look less busted. If they got some clips to list we dont need to show anything here
          [PermissionsReasons.ROLE_DENY]: props.clips.length === 0 && (
            <Alert variant="outlined" severity="info">
              <AlertTitle>Access limited</AlertTitle>
              Your role does have access to create new clips
            </Alert>
          ),
          [PermissionsReasons.UPGRADE_REQUIRED]: (
            <Stack direction="column" spacing={2}>
              <Alert variant="outlined" severity="info">
                <AlertTitle>
                  {props.clips.length === 0
                    ? "Unlock Clip"
                    : "Subscription Required"}
                </AlertTitle>
                {props.clips.length === 0
                  ? "To use Clip and access all its features, you will need a subscription."
                  : "Get back to where you left off, subscribe to Team or Pro to access historic clips and start making new clips."}
              </Alert>
              <Box alignSelf="flex-end" marginTop="auto" display="flex">
                <UpgradeSubscriptionButton
                  variant="contained"
                  text={props.clips.length === 0 ? "start trial" : "subscribe"}
                  size="small"
                />
              </Box>
            </Stack>
          ),
        }}
      >
        {!showMore && createNewClipContent()}
      </PermissionsWrapper>

      {/* Has clips history */}
      {props.clips.length > 0 && !showMore && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flex: 1,
            minHeight: 0,
          }}
        >
          <Typography variant="h5">Recent</Typography>

          <RecentClips
            onSelect={(clip: ClipConfiguration) => {
              props.setClipConfig(clip);
            }}
            mapRef={props.mapRef}
            maxClips={maxClips}
            clips={props.clips}
            draw={props.draw}
          />

          {!!props.clips.length && props.clips.length > maxClips && (
            <Box alignSelf="flex-end" display="flex">
              <Button onClick={() => history.push(Routes.clipManagement)}>More</Button>
            </Box>
          )}
        </Box>
      )}
      {showMore && (
        <HistoricClips {...props} onExit={() => setShowMore(false)} />
      )}
    </Box>
  );
};
